import type { UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CompositionColumnConcept = Omit<smartWorkspaces.ParameterizedConceptDTO, "uuid">;

// ---------------------------------------------------------------------------
// Concept catalog
// ---------------------------------------------------------------------------

/**
 * Maps the fixed (non-classification) composition column ids to their export concept, keyed by the
 * AG Grid `colId` used in `columnsDefinition.instrumentComposition` / `instrumentProposalComposition`.
 * This lets the export be driven directly by the user's persisted column preferences.
 *
 * `CURRENT` compositions show a single `WEIGHT` column; `PROPOSAL` compositions replace it with the
 * pre-proposal weight, the proposed weight and their delta (`PREVIOUS_WEIGHT` / `ENHANCED_WEIGHT` /
 * `DELTA_WEIGHT`). `ACTION` (and any other id without an entry here) is not exportable.
 *
 * `QUANTITY` / `MARKET_PRICE` / `MARKET_VALUE` are the quantity-mode (MDOT-24700) columns. They
 * resolve on both snapshots and on a history export, and — like every concept here — carry no
 * `source`: the snapshot the values come from is the data provider's `snapshot` parameter, and a
 * `source` of its own would blank the column. Rows held in weights export them empty; a cash row
 * carries only its market value.
 *
 * `PREVIOUS_WEIGHT`'s label below is a placeholder: it is only ever exported within a `PROPOSAL`, where
 * `buildCompositionRequest` overrides it with the date-dependent "Composition {date}" header to match
 * the composition table.
 */
export const COMPOSITION_COLUMN_CONCEPTS = {
	NAME: { conceptId: "systemId: instrument:name", representationMode: "ABSOLUTE", label: "Name" },
	DESCRIPTION: { conceptId: "systemId: instrument:description", representationMode: "ABSOLUTE", label: "Description" },
	ISIN: { conceptId: "systemId: instrument:isin", representationMode: "ABSOLUTE", label: "ISIN" },
	CUSIP: { conceptId: "systemId: instrument:cusip", representationMode: "ABSOLUTE", label: "CUSIP" },
	BLOOMBERG_TICKER: {
		conceptId: "systemId: instrument:bloombergTicker",
		representationMode: "ABSOLUTE",
		label: "Bloomberg Ticker",
	},
	ALIAS: { conceptId: "systemId: instrument:alias", representationMode: "ABSOLUTE", label: "Alias" },
	WEIGHT: {
		conceptId: "systemId: instrument:composition:weight",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Weight",
	},
	PREVIOUS_WEIGHT: {
		conceptId: "systemId: instrument:composition:weight",
		representationMode: "ABSOLUTE",
		parameters: {},
		// Placeholder — overridden per-export by `buildCompositionRequest` with the "Composition {date}" header.
		label: "Current weight",
	},
	ENHANCED_WEIGHT: {
		conceptId: "systemId: instrument:composition:proposalWeight",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Proposal weight",
	},
	DELTA_WEIGHT: {
		conceptId: "systemId: instrument:composition:delta",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Delta",
	},
	QUANTITY: {
		conceptId: "systemId: instrument:composition:quantity",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Quantity",
	},
	MARKET_PRICE: {
		conceptId: "systemId: instrument:composition:marketPrice",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Market price",
	},
	MARKET_VALUE: {
		conceptId: "systemId: instrument:composition:marketValue",
		representationMode: "ABSOLUTE",
		parameters: {},
		label: "Market value",
	},
} satisfies Record<string, CompositionColumnConcept>;

export type CompositionColumnConceptsKeys = keyof typeof COMPOSITION_COLUMN_CONCEPTS;

/**
 * Concept ids of the proposal-only weight columns — the proposed weight and its delta against the
 * pre-proposal weight. They belong to the single-investment composition export
 * (`userInstrumentCompositionByInvestment`) but are not served by the bulk
 * `investmentInstrumentPairsByInvestments` provider, so the workspace bulk composition export drops
 * them. The pre-proposal weight (`PREVIOUS_WEIGHT`) is intentionally excluded here: it reuses the plain
 * `WEIGHT` concept id, which the bulk provider does support.
 */
export const PROPOSAL_ONLY_WEIGHT_CONCEPT_IDS: ReadonlySet<string> = new Set([
	COMPOSITION_COLUMN_CONCEPTS.ENHANCED_WEIGHT.conceptId,
	COMPOSITION_COLUMN_CONCEPTS.DELTA_WEIGHT.conceptId,
]);

/** AG Grid `colId` of the export-only Risk Model column — no universe table column mirrors it. */
export const RISK_MODEL_COL_ID = "RISK_MODEL";

/** Risk Model identity column — part of the universe instrument-list export, not the composition table. */
export const RISK_MODEL_CONCEPT: CompositionColumnConcept = {
	conceptId: "systemId: instrument:riskModel",
	label: "Risk Model",
};

/** Identity column ids, in display order, shared by the composition and universe instrument-list exports. */
export const INSTRUMENT_IDENTITY_COL_IDS = [
	"NAME",
	"DESCRIPTION",
	"ISIN",
	"CUSIP",
	"BLOOMBERG_TICKER",
	"ALIAS",
] as const;

// ---------------------------------------------------------------------------
// Concept helpers
// ---------------------------------------------------------------------------

export function withUuid(concept: CompositionColumnConcept): smartWorkspaces.ParameterizedConceptDTO {
	return { uuid: crypto.randomUUID(), ...concept };
}

/**
 * Maps a user instrument classification (tag / quality / score) to its smart-workspace conceptId,
 * mirroring `classificationConceptId` in `buildCompositionExportConfig`.
 */
function classificationConceptId(classification: UserInstrumentClassificationDto): string {
	if (classification.fieldType === "TAG") {
		return `instrumentTagId: ${classification.classificationUuid}`;
	}
	if (classification.fieldType === "QUALITY") {
		return `instrumentQualityId: ${classification.classificationUuid}`;
	}
	return `instrumentScoreId: ${classification.classificationUuid}`;
}

/** Builds a fresh parametrized concept (new UUID) for a user instrument classification column. */
function buildClassificationConcept(
	classification: UserInstrumentClassificationDto,
): smartWorkspaces.ParameterizedConceptDTO {
	return {
		uuid: crypto.randomUUID(),
		conceptId: classificationConceptId(classification),
		label: classification.name ?? classification.classificationUuid ?? "",
		source: "CURRENT",
	};
}

/**
 * Resolves a single persisted column id to its export concept. Fixed columns come from
 * `COMPOSITION_COLUMN_CONCEPTS`; every other id is treated as a classification uuid and resolved
 * against the user's classification metadata. Returns `null` for non-exportable columns (e.g. ACTION).
 */
export function columnIdToConcept(
	colId: string,
	classifications: UserInstrumentClassificationDto[],
): smartWorkspaces.ParameterizedConceptDTO | null {
	const fixed = COMPOSITION_COLUMN_CONCEPTS[colId as keyof typeof COMPOSITION_COLUMN_CONCEPTS];
	if (fixed) {
		return withUuid(fixed);
	}
	const classification = classifications.find((c) => c.classificationUuid === colId);
	if (classification) {
		return buildClassificationConcept(classification);
	}
	return null;
}
